import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AnswerCards,
  AnswersContainer,
  AnswerWrapper,
  CodeSticker,
  Controls,
  Hint,
  QRCodeFrame,
  QuestionContainer,
  RoomBar,
  RoundButton,
  TableContainer,
  Waiting,
} from "./Table.styles";
import Card from "../Card";
import { useGameContext } from "../../contexts/GameContext";
import { Button, CircularProgress, Zoom } from "@mui/material";
import { useNextQuestion } from "../../hooks/Game/useNextQuestion";
import { useSessionContext } from "../../contexts/SessionContext";
import { useVoteAnswer } from "../../hooks/Game/useVoteAnswer";
import { toast } from "material-react-toastify";
import { GameCard } from "../../types/Card";
import QRCode from "react-qr-code";
import { PATHS } from "../../routes";
import { useSoundContext } from "../../contexts/SoundContext";
import Clock from "../Clock/Clock";
import Icon from "../ui/Icon";
import Tag from "../ui/Tag";
import ModalCard from "../ui/ModalCard";
import Confetti from "../ui/Confetti";
import { APP_NAME } from "../../brand";
import { colors } from "../../style/tokens";

const ADVANCE_LABELS: Record<string, string> = {
  lobby: "Começar partida",
  answering: "Pular para votação",
  voting: "Encerrar votação",
  results: "Próxima pergunta",
  finished: "Fim de jogo",
};

type TableProps = {};

const Table: React.FC<TableProps> = () => {
  const [openAnswer, setOpenAnswer] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [visibleAnswers, setVisibleAnswers] = useState<any>({});
  const {
    questionCard,
    code,
    showHand,
    tableAnswers,
    selectedCards,
    setTableAnswers,
    votingStarted,
    votingFinished,
    phase,
    gameStarted,
    adminId,
    onlinePlayers,
    finished,
    getGameState,
  } = useGameContext();
  const { playPopAudio, playClickAudio, playWinAudio } = useSoundContext();
  const { user_id: userId } = useSessionContext();
  const [voted, setVoted] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const lastQuestion = useRef<GameCard>();
  const toastId = useRef<number | string>();

  const isAdmin = userId === adminId;

  const { nextQuestion, error: nextError } = useNextQuestion(code);
  const {
    voteAnswer,
    loading: voteLoading,
    error: voteError,
  } = useVoteAnswer(code);

  const hasError = !!nextError || !!voteError;

  useEffect(() => {
    if (hasError) {
      getGameState(code);
    }
  }, [hasError, getGameState, code]);

  const maxVotes = useMemo(
    () => Math.max(1, ...tableAnswers.map(({ votes }) => votes?.length || 0)),
    [tableAnswers],
  );

  const userHasWon = useMemo(
    () =>
      votingFinished &&
      !!tableAnswers.find(
        (ans) => ans.user_id === userId && maxVotes === ans.votes?.length,
      ),
    [maxVotes, tableAnswers, userId, votingFinished],
  );

  const orderedAnswers = useMemo(() => {
    if (votingFinished) {
      return [...tableAnswers].sort((a, b) => (
        (b.votes?.length || 0) - (a.votes?.length || 0)
      ));
    }

    return tableAnswers;
  }, [tableAnswers, votingFinished]);

  const handleNextQuestion = async () => {
    if (advancing) return;
    setAdvancing(true);
    setLoading(true);
    setVisibleAnswers({});
    playPopAudio();
    try {
      await nextQuestion();
    } finally {
      setAdvancing(false);
      setLoading(false);
    }
  };

  const handleVote = async (user_id: string) => {
    setVoted(true);
    setTimeout(() => setVoted(false), 2000);
    let previous: any[] = [];
    setTableAnswers((t) => {
      previous = t;
      return t.map((answer) => ({
        ...answer,
        votes:
          answer.user_id === user_id
            ? [...answer.votes, userId as string]
            : answer.votes,
      }));
    });
    playClickAudio();
    try {
      await voteAnswer(user_id);
    } catch {
      // server rejected the vote, drop the optimistic one
      setTableAnswers(previous as any);
    }
  };

  const shareRoom = async () => {
    if (navigator?.share) {
      try {
        await navigator.share({
          title: APP_NAME,
          text: "O jogo de Cartas Pesadas (muitos Pesadas)!",
          url: `${window.location.origin}${PATHS.GAME.PLAY(code)}`,
        });
      } catch (error) {
        toast.error("Não foi possível compartilhar");
      }
    } else {
      toast.error("Seu navegador não suporta compartilhamento");
    }
  };

  const userHasVoted = useMemo(
    () =>
      !!tableAnswers?.find((answer: any) => answer?.votes?.includes?.(userId)),
    [tableAnswers, userId],
  );

  const userHasPlayed = useMemo(
    () => !!tableAnswers?.find((answer: any) => answer?.user_id === userId),
    [tableAnswers, userId],
  );

  useEffect(() => {
    if (userHasWon) {
      playWinAudio();
    }
  }, [playWinAudio, questionCard?.id, userHasWon]);

  useEffect(() => {
    setOpenAnswer(-1);

    if (!tableAnswers.length) {
      setVisibleAnswers({});
    }

    for (let i = 0; i < tableAnswers.length; i++) {
      setTimeout(
        () =>
          setVisibleAnswers((v: any) => ({
            ...v,
            [i]: true,
          })),
        i * 300,
      );
    }
  }, [tableAnswers.length]);

  useEffect(() => {
    if (votingFinished && isAdmin) {
      if (toastId.current && toast.isActive(toastId.current)) {
        try {
          toast.dismiss(toastId.current);
          toastId.current = undefined;
        } catch {
          //
        }
      }
      toastId.current = toast.dark("Passe para a próxima pergunta.");
    }
  }, [isAdmin, votingFinished]);

  useEffect(() => {
    if (
      votingStarted &&
      onlinePlayers.length > 1 &&
      tableAnswers.length &&
      !votingFinished
    ) {
      if (toastId.current && toast.isActive(toastId.current)) {
        try {
          toast.dismiss(toastId.current);
          toastId.current = undefined;
        } catch {
          //
        }
      }
      toastId.current = toast.dark("Vote na melhor resposta.");
    }
  }, [
    onlinePlayers.length,
    tableAnswers.length,
    votingFinished,
    votingStarted,
  ]);

  useEffect(() => {
    if (gameStarted && lastQuestion.current?.id !== questionCard?.id) {
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
      lastQuestion.current = questionCard;
    }
  }, [gameStarted, questionCard]);

  useEffect(() => {
    if (!gameStarted) return;
    if (toastId.current && toast.isActive(toastId.current)) {
      toast.dismiss(toastId.current);
      toastId.current = undefined;
    }
    toastId.current = toast.dark("O jogo começou.");
  }, [gameStarted]);

  const cardsLeftToSelect = (questionCard?.answers || 1) - selectedCards.length;

  const copyToClipboard = () => {
    try {
      navigator.clipboard.writeText(code);
      toastId.current = toast.success("Código copiado.");
    } catch (error) {
      toastId.current = toast.error("Não foi possível copiar o código.");
    }
  }

  if (finished) {
    return null;
  }

  return (
    <TableContainer $loading={loading}>
      {userHasWon && onlinePlayers.length > 1 && (
        <Confetti key={questionCard?.id} />
      )}

      <RoomBar>
        <RoundButton onClick={shareRoom} aria-label="Compartilhar sala">
          <Icon name="share" size={18} />
        </RoundButton>
        <CodeSticker onClick={copyToClipboard} aria-label="Copiar código">
          <small>Sala</small>
          {code}
          <Icon name="copy" size={16} />
        </CodeSticker>
        <RoundButton onClick={() => setShowQR(true)} aria-label="QR code">
          <Icon name="qr" size={18} />
        </RoundButton>
      </RoomBar>

      <Controls>
        {isAdmin && (
          <Button
            onClick={handleNextQuestion}
            variant="contained"
            color={phase === "lobby" ? "primary" : "success"}
            size={phase === "lobby" ? "large" : "medium"}
            disabled={advancing}
            endIcon={
              advancing ? (
                <CircularProgress color="inherit" size={16} />
              ) : phase === "lobby" ? (
                <Icon name="play" />
              ) : (
                <Icon name="next" />
              )
            }
          >
            {ADVANCE_LABELS[phase] || "Avançar"}
          </Button>
        )}

        <Clock />
      </Controls>

      <QuestionContainer $loading={loading || !gameStarted}>
        {questionCard && (
          <Card {...questionCard} text={loading ? "" : questionCard.text} />
        )}
      </QuestionContainer>

      {showHand && !loading && gameStarted && !userHasPlayed && (
        <Hint className="anim-pop">
          <strong>
            Escolha {cardsLeftToSelect} resposta
            {cardsLeftToSelect === 1 ? "" : "s"}
          </strong>
          {!!tableAnswers?.length && (
            <Tag $tone="teal" $size="md">
              <Icon name="check" size={14} strokeWidth={3} />
              {tableAnswers.length} jogador{tableAnswers.length === 1 ? "" : "es"} já
              escolhe{tableAnswers.length === 1 ? "u" : "ram"}
            </Tag>
          )}
        </Hint>
      )}

      {!isAdmin && !gameStarted && (
        <Waiting>
          <Icon name="time" size={28} />
          Aguarde o dono da sala iniciar a partida.
        </Waiting>
      )}

      <AnswersContainer $show={!showHand && !!tableAnswers?.length}>
        {orderedAnswers?.map(
          ({ user_id: cardPlayerId, cards, votes, user }, i) => {
            const isWinner = maxVotes === votes?.length;
            const ownAnswer =
              cardPlayerId === userId && onlinePlayers.length !== 2;
            const votedHere = votes?.includes(userId!);

            return (
              <AnswerCards
                key={cardPlayerId}
                $count={cards?.length || 1}
                $visible={visibleAnswers[i]}
                $voteStarted={votingStarted}
                $voteFinished={votingFinished}
                $won={onlinePlayers.length > 1 && votingFinished && isWinner}
              >
                {onlinePlayers.length > 1 && (
                  <>
                    <Zoom
                      in={votingStarted && votingFinished}
                      timeout={100}
                      unmountOnExit
                      style={{
                        transitionDelay: "200ms",
                      }}
                    >
                      <Tag $tone={isWinner ? "gold" : "neutral"} $size="md">
                        <Icon name={isWinner ? "crown" : "vote"} size={16} />
                        {votes?.length} voto{votes?.length === 1 ? "" : "s"}
                      </Tag>
                    </Zoom>
                    <Zoom
                      in={votingStarted && !votingFinished}
                      timeout={100}
                      unmountOnExit
                      style={{
                        transitionDelay: "200ms",
                      }}
                    >
                      <Button
                        size="small"
                        variant="contained"
                        color={votedHere ? "info" : ownAnswer ? "inherit" : "success"}
                        onClick={() => handleVote(cardPlayerId!)}
                        endIcon={
                          !ownAnswer &&
                          (voteLoading ? (
                            <CircularProgress color="inherit" size={14} />
                          ) : votedHere ? (
                            <Icon name="check" size={16} strokeWidth={3} />
                          ) : (
                            <Icon name="vote" size={16} />
                          ))
                        }
                        disabled={
                          voteLoading || voted || userHasVoted || ownAnswer
                        }
                      >
                        {ownAnswer
                          ? "Sua resposta"
                          : votedHere
                            ? "Votou"
                            : "Votar"}
                      </Button>
                    </Zoom>
                  </>
                )}
                {cards?.map(
                  (cardProps, index) =>
                    !!cardProps && (
                      <AnswerWrapper
                        key={cardProps.id}
                        $open={openAnswer === i}
                        onClick={() => setOpenAnswer((o) => (o === i ? -1 : i))}
                      >
                        <Card
                          {...(cardProps as any)}
                          player={
                            index >= cards.length - 1 &&
                            !!votingFinished &&
                            onlinePlayers.length > 1 &&
                            user?.name
                          }
                        />
                      </AnswerWrapper>
                    ),
                )}
              </AnswerCards>
            );
          },
        )}
      </AnswersContainer>

      <ModalCard
        open={showQR}
        onClose={() => setShowQR(false)}
        title="Chame a galera"
        icon="qr"
        tone="teal"
        width={360}
      >
        <QRCodeFrame>
          <div>
            <QRCode
              value={`${window.location.origin}${PATHS.GAME.PLAY(code)}`}
              fgColor={colors.ink}
            />
          </div>
          <Tag $tone="gold" $size="md">Sala {code}</Tag>
        </QRCodeFrame>
      </ModalCard>
    </TableContainer>
  );
};

export default Table;
